import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Cell, Spacer } from '@vinted/web-ui';
import classNames from 'classnames';
import { AuthModal } from 'components/Auth';
import FaqEntryUrl from 'components/FaqEntryUrl';
import HeaderHelpButton from 'components/HeaderHelpButton';
import HeaderLogo from 'components/HeaderLogo';
import LanguageSelector from 'components/LanguageSelector';
import { SearchSubscribeButton } from 'components/SavedSearches';
import AppBanner from 'components/AppBanner';
import { FaqEntryType } from 'constants/faq-entry';
import { AccessChannel } from 'constants/index';
import { getScreenName } from 'state/screen/selectors';
import { getAbTestByName } from 'state/ab-tests/selectors';
import { Screen } from 'constants/tracking/screens';
import { getUserId } from 'state/session/selectors';
import { getIsPopularInCatalog } from 'state/catalog-filters/selectors';
import CatalogNavigation from './CatalogNavigation';
import HeaderLoginButton from './HeaderLoginButton';
import HeaderToggleMobileMenu from './HeaderToggleMobileMenu';
import HeaderUploadItem from './HeaderUploadItem';
import HeaderUserActions from './HeaderUserActions';
import SearchBar from './SearchBar';
import SideNavigation from './SideNavigation';
import UserMenu from './UserMenu';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const Header = _ref => {
  let {
    languages,
    catalogTree,
    isInCatalog,
    searchType,
    portal,
    impressumUrl,
    isBusinessAccountLinksVisible,
    isOurPlatformVisible,
    injectedMenus
  } = _ref;
  const userId = useSelector(getUserId);
  const screenName = useSelector(getScreenName);
  const webToAppAbTest = useSelector(getAbTestByName('web_2_app_conversion_v1'));
  const isPopularCatalog = useSelector(getIsPopularInCatalog);
  const showSearchSubscribeButton = isInCatalog && userId && !isPopularCatalog;
  const {
    0: isSideNavigationOpen,
    1: setIsSideNavigationOpen
  } = useState(false);

  function toggleSideNavigation() {
    setIsSideNavigationOpen(prevState => !prevState);
  }

  function renderAppBannerAbTest() {
    if ((webToAppAbTest === null || webToAppAbTest === void 0 ? void 0 : webToAppAbTest.variant) === 'on' && screenName === Screen.NewsFeed) {
      return /*#__PURE__*/_jsx(AppBanner, {
        isHeader: true
      });
    }

    return null;
  }

  function renderHeaderHelpButton() {
    return /*#__PURE__*/_jsx(FaqEntryUrl, {
      type: FaqEntryType.VintedGuide,
      accessChannel: AccessChannel.VintedGuide,
      render: url => /*#__PURE__*/_jsx(HeaderHelpButton, {
        url: url
      })
    });
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      className: classNames('l-header js-header', {
        'is-active': isSideNavigationOpen
      }),
      children: [renderAppBannerAbTest(), /*#__PURE__*/_jsx("header", {
        className: "l-header__main",
        children: /*#__PURE__*/_jsx("div", {
          className: "container u-flexbox u-align-items-center u-fill-height",
          children: /*#__PURE__*/_jsxs("div", {
            className: "l-header__content",
            children: [/*#__PURE__*/_jsx(HeaderLogo, {
              portal: portal
            }), /*#__PURE__*/_jsx("div", {
              className: "l-header__search",
              children: /*#__PURE__*/_jsx(SearchBar, {
                isInCatalog: isInCatalog,
                searchType: searchType
              })
            }), /*#__PURE__*/_jsxs("div", {
              className: "l-header__actions",
              children: [userId && /*#__PURE__*/_jsx("div", {
                className: "user-actions",
                children: /*#__PURE__*/_jsx(HeaderUserActions, {})
              }), /*#__PURE__*/_jsxs("div", {
                className: "u-desktops-only u-flexbox u-align-items-center",
                children: [userId && /*#__PURE__*/_jsx("div", {
                  className: "u-position-relative u-ui-padding-left-regular u-ui-padding-right-x-large",
                  children: /*#__PURE__*/_jsx(UserMenu, {
                    injectedMenus: injectedMenus
                  })
                }), !userId && /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(HeaderLoginButton, {}), /*#__PURE__*/_jsx(Spacer, {
                    orientation: Spacer.Orientation.Vertical
                  })]
                }), /*#__PURE__*/_jsx(HeaderUploadItem, {}), /*#__PURE__*/_jsx("div", {
                  className: "u-ui-padding-left-regular",
                  children: renderHeaderHelpButton()
                }), /*#__PURE__*/_jsx("div", {
                  className: "language-selector",
                  children: /*#__PURE__*/_jsx(LanguageSelector, {
                    initialLanguages: languages
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx("div", {
              className: "l-header__toggle",
              children: /*#__PURE__*/_jsx(HeaderToggleMobileMenu, {
                isActive: isSideNavigationOpen,
                onToggle: toggleSideNavigation
              })
            })]
          })
        })
      }), /*#__PURE__*/_jsxs("div", {
        className: "l-header__navigation",
        children: [isSideNavigationOpen && /*#__PURE__*/_jsx("div", {
          className: "u-mobiles-only",
          children: /*#__PURE__*/_jsx(SideNavigation, {
            catalogTree: catalogTree,
            impressumUrl: impressumUrl,
            isBusinessAccountLinksVisible: isBusinessAccountLinksVisible
          })
        }), /*#__PURE__*/_jsx("div", {
          className: "u-desktops-only",
          children: /*#__PURE__*/_jsx("div", {
            className: "container",
            children: /*#__PURE__*/_jsx(CatalogNavigation, {
              tree: catalogTree,
              impressumUrl: impressumUrl,
              isBusinessAccountLinksVisible: isBusinessAccountLinksVisible,
              isOurPlatformVisible: isOurPlatformVisible
            })
          })
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: "l-search",
      children: /*#__PURE__*/_jsx(Cell, {
        styling: Cell.Styling.Tight,
        children: /*#__PURE__*/_jsx("div", {
          className: "u-ui-padding-vertical-regular",
          children: /*#__PURE__*/_jsx("div", {
            className: "container",
            children: /*#__PURE__*/_jsxs("div", {
              className: "u-flexbox",
              children: [/*#__PURE__*/_jsx("div", {
                className: "u-fill-width",
                children: /*#__PURE__*/_jsx(SearchBar, {
                  isInCatalog: isInCatalog,
                  searchType: searchType
                })
              }), showSearchSubscribeButton && /*#__PURE__*/_jsx("div", {
                className: "u-phones-only u-ui-margin-left-medium",
                children: /*#__PURE__*/_jsx(SearchSubscribeButton, {})
              })]
            })
          })
        })
      })
    }), !userId && /*#__PURE__*/_jsx(AuthModal, {})]
  });
};

export default Header;